import './RankingsPerHour.css'

import classNames from 'classnames'
import { EmptyResults, InputDropdown, StoreSearchInput } from 'components'
import ToggleButton from 'components/ToggleButton'
import { selectLanguageBasedOnBrowserLocale } from 'locale'
import PropTypes from 'prop-types'
import { CollapsedHours, Days, generateLegend, Hours, HoursChart, Legend } from 'rankings/HoursChart'
import React from 'react'
import { countryIdToString, DEFAULT_CURRENCY } from 'utils'

import { INDICATORS, PERCENT_INDICATORS } from './constants'
import HoursSummary from './HoursSummary/HoursSummary'

function RankingsPerHour(
  {
    countries,
    dataPerHour,
    indicator,
    isCollapsed,
    isHourlyCountriesIndicator,
    isWeekly,
    selectedCountry,
    selectedCountryCompared,
    selectedStore,
    selectedStoreCompared,
    setSelectedCountry,
    setSelectedCountryCompared,
    setSelectedStore,
    setSelectedStoreCompared,
    storesRequestConfig,
    toggleCollapsed,
    toggleWeekly,
    usePercentages,
  },
  { t },
) {
  const hoursChartProps = isCollapsed ? { fromHour: 9 } : {}
  const dates = dataPerHour.map(({ date }) => date)
  const legend = generateLegend(dataPerHour)
  let options = []
  let optionsCompared = []

  if (isHourlyCountriesIndicator) {
    const countryNameLocale = `countryName${selectLanguageBasedOnBrowserLocale().toUpperCase()}`
    options = countries.map(({ countryID, ...country }) => ({
      icon: <div className={classNames('flag', `flag-${countryIdToString(countryID)}`)} />,
      label: country[countryNameLocale],
      value: countryID,
    }))
    optionsCompared = [{ label: '-', value: null }, ...options.filter(({ value }) => value !== selectedCountry)]
  }
  const isPercentIndicator = PERCENT_INDICATORS.includes(indicator)

  return (
    <div className="rankings-per-hour">
      <div className="rankings-per-hour-compare">
        <div>{t('rankings.comparison')}:</div>
      </div>
      {isHourlyCountriesIndicator && (
        <div className="rankings-per-hour-countries">
          <InputDropdown
            onChange={setSelectedCountry}
            options={options.filter(({ value }) => value !== selectedCountryCompared)}
            selectedValue={selectedCountry}
          />
          <InputDropdown onChange={setSelectedCountryCompared} options={optionsCompared} selectedValue={selectedCountryCompared} />
        </div>
      )}
      {!isHourlyCountriesIndicator && (
        <div>
          <StoreSearchInput
            onStoreSelected={setSelectedStore}
            requestConfig={storesRequestConfig}
            selectedCountry={selectedCountry}
            selectedStore={selectedStore}
          />
          <StoreSearchInput
            onStoreSelected={setSelectedStoreCompared}
            requestConfig={storesRequestConfig}
            selectedCountry={selectedCountryCompared}
            selectedStore={selectedStoreCompared}
          />
        </div>
      )}
      {isPercentIndicator && (
        <ToggleButton
          checked={isWeekly}
          labelOff={t('rankings.dailyRange')}
          labelOn={t('rankings.weeklyRange')}
          onChange={toggleWeekly}
        />
      )}
      {!dataPerHour.length && <EmptyResults />}
      {dataPerHour.length > 0 && (
        <div>
          <Days dates={dates} />
          <CollapsedHours isCollapsed={isCollapsed} fromHour={0} toHour={8} onClick={toggleCollapsed} />
          <div style={{ display: 'flex' }}>
            <Hours isCollapsed={isCollapsed} fromHour={9} onClick={toggleCollapsed} />
            {dataPerHour.map((data, index) => (
              <HoursChart
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                data={data.countries}
                legend={legend}
                {...hoursChartProps}
              />
            ))}
          </div>
          <Legend colors={legend} currency={DEFAULT_CURRENCY} usePercentages={usePercentages} />
          {usePercentages && (
            <HoursSummary
              currency={DEFAULT_CURRENCY}
              showOrders={INDICATORS.STORES_ECOMMERCE_ORDERS === indicator || INDICATORS.COMPANIES_ECOMMERCE_ORDERS === indicator}
              rankingHourlyCountries={dataPerHour}
            />
          )}
        </div>
      )}
    </div>
  )
}

RankingsPerHour.contextTypes = {
  t: PropTypes.func,
}

RankingsPerHour.propTypes = {
  countries: PropTypes.arrayOf(PropTypes.object),
  dataPerHour: PropTypes.arrayOf(PropTypes.object),
  indicator: PropTypes.string.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  isHourlyCountriesIndicator: PropTypes.bool.isRequired,
  isWeekly: PropTypes.bool.isRequired,
  selectedCountry: PropTypes.number,
  selectedCountryCompared: PropTypes.number,
  selectedStore: PropTypes.number,
  selectedStoreCompared: PropTypes.number,
  setSelectedCountry: PropTypes.func.isRequired,
  setSelectedCountryCompared: PropTypes.func.isRequired,
  setSelectedStore: PropTypes.func.isRequired,
  setSelectedStoreCompared: PropTypes.func.isRequired,
  storesRequestConfig: PropTypes.object,
  toggleCollapsed: PropTypes.func.isRequired,
  toggleWeekly: PropTypes.func.isRequired,
  usePercentages: PropTypes.bool.isRequired,
}

export default RankingsPerHour
